import { IconSvg } from 'Components/shared/Icon/index.generated';
import { UserAvatar } from 'Components/shared/UserAvatar';
import { UserAvatarProps } from 'Components/shared/UserAvatar/types';
import { MobXProviderContext, observer } from 'mobx-react';
import { ContactModelBase } from 'Models/ContactModel';
import { ContactModelV2 } from 'Models/ContactModelV2';
import React, { useCallback } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { Button } from '~/lib/ui/button';
import { cn } from '~/lib/utils';
import { AVATAR_CONFIG } from '../../../../utils/avatar_dimensions';
import EntityBasicInfo from '../../../EntityBasicInfo';
import { EntityBasicInfoProps } from '../../../EntityBasicInfo/index.types';
import { useContactInfo } from '../../hooks/useContactInfo';
import { ContactDetailsActions } from './ContactDetailsActions';
import { ContactDetailsHeaderProps } from './index.types';

const { size, fontSize } = AVATAR_CONFIG.HEADER_DIMENSIONS;

export const ContactDetailsHeader = observer(
  ({ contact, goBack, closePanel }: ContactDetailsHeaderProps) => {
    const { person: personStore } =
      React.useContext<RootStoreProps>(MobXProviderContext);

    const { name, image, presence, statusMessage } = useContactInfo({
      contact,
    });

    const renderEntityAttribute = useCallback(() => {
      const commonProps = {
        name,
        statusMessage,
        labels: [],
      } as EntityBasicInfoProps;

      let isSelf = false;

      if (contact instanceof ContactModelV2) {
        // Bhive contacts have department, and external contacts have organization
        const departmentOrOrganization =
          contact.department || contact.organization;
        if (departmentOrOrganization) {
          commonProps.labels.push(departmentOrOrganization);
        }

        contact.role && commonProps.labels.push(contact.role);

        if (
          contact.source === 'Bhive' &&
          contact.sourceId === personStore.loggedInPersonId.toString()
        ) {
          isSelf = true;
        }
      }

      return (
        <EntityBasicInfo {...commonProps}>
          {isSelf ? null : <ContactDetailsActions contact={contact} />}
        </EntityBasicInfo>
      );
    }, [contact, name, statusMessage, personStore.loggedInPersonId]);

    const renderAvatar = useCallback(() => {
      const commonProps = {
        size,
        fontSize,
        iconSize: AVATAR_CONFIG.getIconSize(size),
        image,
        name,
        presence,
        key: `contact-details-avatar-${contact?.id}`,
      } as UserAvatarProps;

      if (contact instanceof ContactModelBase) {
        delete commonProps.image;
        delete commonProps.name;
        delete commonProps.presence;
      }

      return (
        <UserAvatar
          {...commonProps}
          key={`contact-details-avatar-${contact?.id}-${contact?.phoneNumber}`}
        />
      );
    }, [contact, image, name, presence]);

    return (
      <>
        <div
          className={cn('flex items-end justify-end', {
            'justify-between': !!goBack,
          })}
        >
          {goBack && (
            <Button
              variant="ghost"
              size="icon"
              className="p-0 hover:bg-transparent"
              onClick={goBack}
            >
              {IconSvg['back-arrow']}
            </Button>
          )}

          <Button
            variant="ghost"
            size="icon"
            className="p-0 hover:bg-transparent"
            onClick={closePanel}
          >
            {IconSvg['cross']}
          </Button>
        </div>

        <div className="flex items-center justify-center">{renderAvatar()}</div>

        {renderEntityAttribute()}
      </>
    );
  }
);
